import { X } from 'lucide-react';
import { useEffect, useState } from 'react';

import { IconButton } from '@/design-system';
import { cn } from '@/shared/cn';
import {
  dismissalKey,
  publicBannerFromConfig,
} from '@/versions/v1/modules/admin/AnnouncementBannerScene.transform';
import { SEED_ANNOUNCEMENT_BANNER_CONFIG } from '@/versions/v1/shared/admin-seed';

export type AnnouncementBannerProps = {
  message: string;
  updatedAt: string;
};

function safeGet(key: string): string | null {
  try {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof window === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    // Storage failure degrades to always-visible — fail open.
  }
}

/**
 * Fixed top band shown to signed-in users when the admin has enabled an
 * announcement. Dismissal is per-browser, keyed on `updatedAt` so editing
 * the message re-shows the banner to everyone.
 */
export function AnnouncementBanner({ message, updatedAt }: AnnouncementBannerProps) {
  const key = dismissalKey(updatedAt);

  const [visible, setVisible] = useState(() => true);

  useEffect(() => {
    const dismissed = safeGet(key);
    if (dismissed === '1') {
      setVisible(false);
    }
  }, [key]);

  const handleDismiss = () => {
    safeSet(key, '1');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      role="region"
      aria-label="Announcement"
      className={cn(
        'flex shrink-0 items-center justify-between gap-3',
        'border-b border-[var(--glass-border)]',
        'bg-[var(--glass-surface-soft)] px-4 py-2',
        'text-sm text-zinc-700 dark:text-zinc-200',
      )}
    >
      <p className="min-w-0 flex-1 truncate">{message}</p>
      <IconButton label="Dismiss announcement" onClick={handleDismiss}>
        <X className="h-4 w-4" aria-hidden />
      </IconButton>
    </div>
  );
}

/** Reads seeded admin config — mounted in app layout chrome. */
export function SeededAnnouncementBanner() {
  const banner = publicBannerFromConfig(SEED_ANNOUNCEMENT_BANNER_CONFIG);
  if (!banner) return null;
  return <AnnouncementBanner message={banner.message} updatedAt={banner.updatedAt} />;
}
